import { FC, useRef } from 'react';
import { isEqual } from 'lodash';
import { PinturaImageState } from '@pqina/pintura';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { ImageEditor } from './ImageEditor';
import { DecorationOverlay } from './DecorationOverlay';
import { useEditorStore } from './EditorStore';
import { useArtworkStepperStore } from '../../stores/ArtworkStepperStore';

/**
 * Renders the Pintura-based photo editor for the image file currently held
 * in the ArtworkStepperStore, plus the decoration overlay.
 *
 * If the image cannot be loaded (unreadable file, see UploadPhotoStep),
 * the user is sent back to the upload step with an explanatory toast
 * instead of leaving a broken editor behind.
 */
export const PhotoArtworkEditor: FC = () => {
  const lastState = useRef<PinturaImageState | null>(null);

  const { setImageState } = useEditorStore();
  const { imageFile, setActiveStep } = useArtworkStepperStore();
  const { t } = useTranslation();

  const handleUpdate = (newState: PinturaImageState) => {
    if (!isEqual(lastState.current, newState)) {
      lastState.current = newState;
      setImageState(newState);
    }
  };

  const handleLoadError = () => {
    toast.error(t('fileNotReadableError'));
    setActiveStep(0);
  };

  return (
    <>
      {imageFile && (
        <>
          <ImageEditor
            imageFile={imageFile}
            onUpdate={handleUpdate}
            onLoadError={handleLoadError}
          />
          <DecorationOverlay />
        </>
      )}
    </>
  );
};
